import type { AudioTheme } from './audioThemes.js';
import { bindAudioLifecycle } from './audioLifecycle.js';
import { playThemedSfx } from './themedSfx.js';

export type GearSoundKind = 'extend' | 'backtrack' | 'pairComplete' | 'win';

export interface GearSoundEvent {
  kind: GearSoundKind;
  pathLength?: number;
}

/** Base frequency (Hz) for extend clink; pitch rises with path length (§11). */
export function gearExtendPitchHz(pathLength: number): number {
  return 720 + Math.min(pathLength, 24) * 28;
}

/** Backtrack uses lower pitch with reverse feel (§11). */
export function gearBacktrackPitchHz(pathLength: number): number {
  return gearExtendPitchHz(pathLength) * 0.82;
}

export class GearSoundPlayer {
  private ctx: AudioContext | null = null;
  private enabled: boolean;
  /** Тема стиля уровня: если задана — звуки играют в ней, иначе (gear) — собственные шестерёнчатые. */
  private theme: AudioTheme | null = null;

  constructor(enabled = true) {
    this.enabled = enabled;
    bindAudioLifecycle(
      () => this.ctx,
      () => this.enabled,
    );
  }

  setTheme(theme: AudioTheme | null): void {
    this.theme = theme;
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  /** Call on first user gesture to unlock Web Audio. */
  resume(): void {
    if (!this.enabled) return;
    const ctx = this.getContext();
    if (ctx.state !== 'running') {
      void ctx.resume();
    }
  }

  play(event: GearSoundEvent): void {
    if (!this.enabled) return;
    const ctx = this.getContext();
    if (ctx.state !== 'running') {
      void ctx.resume(); // 'suspended' (автоплей) или 'interrupted' (iOS) — этот звук потерян, следующие пойдут
      return;
    }

    if (this.theme) {
      playThemedSfx(ctx, this.theme, event);
      return;
    }

    switch (event.kind) {
      case 'extend':
        this.playClink(ctx, gearExtendPitchHz(event.pathLength ?? 2), 0.22);
        break;
      case 'backtrack':
        this.playClink(ctx, gearBacktrackPitchHz(event.pathLength ?? 2), 0.14);
        break;
      case 'pairComplete':
        this.playRatchetThud(ctx);
        break;
      case 'win':
        this.playWinGears(ctx);
        break;
    }
  }

  private getContext(): AudioContext {
    if (!this.ctx) {
      const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.ctx = new Ctx();
    }
    return this.ctx;
  }

  private playClink(ctx: AudioContext, freq: number, gain: number): void {
    const t0 = ctx.currentTime;
    const master = ctx.createGain();
    master.gain.setValueAtTime(gain, t0);
    master.gain.exponentialRampToValueAtTime(0.001, t0 + 0.12);
    master.connect(ctx.destination);

    const osc = ctx.createOscillator();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(freq, t0);
    osc.frequency.exponentialRampToValueAtTime(freq * 0.55, t0 + 0.08);
    const oscGain = ctx.createGain();
    oscGain.gain.setValueAtTime(0.35, t0);
    oscGain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.07);
    osc.connect(oscGain);
    oscGain.connect(master);
    osc.start(t0);
    osc.stop(t0 + 0.1);

    const bufferSize = Math.floor(ctx.sampleRate * 0.04);
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0)!;
    for (let i = 0; i < bufferSize; i++) {
      data[i] = (Math.random() * 2 - 1) * (1 - i / bufferSize);
    }
    const noise = ctx.createBufferSource();
    noise.buffer = buffer;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = freq * 1.4;
    bp.Q.value = 8;
    const noiseGain = ctx.createGain();
    noiseGain.gain.setValueAtTime(0.5, t0);
    noiseGain.gain.exponentialRampToValueAtTime(0.001, t0 + 0.04);
    noise.connect(bp);
    bp.connect(noiseGain);
    noiseGain.connect(master);
    noise.start(t0);
    noise.stop(t0 + 0.05);
  }

  private playRatchetThud(ctx: AudioContext): void {
    const t0 = ctx.currentTime;
    for (let i = 0; i < 4; i++) {
      const t = t0 + i * 0.028;
      this.playClink(ctx, 580 + i * 40, 0.1 - i * 0.015);
      const thud = ctx.createOscillator();
      thud.type = 'sine';
      thud.frequency.setValueAtTime(90 - i * 8, t);
      const g = ctx.createGain();
      g.gain.setValueAtTime(i === 3 ? 0.18 : 0.06, t);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.08);
      thud.connect(g);
      g.connect(ctx.destination);
      thud.start(t);
      thud.stop(t + 0.09);
    }
  }

  private playWinGears(ctx: AudioContext): void {
    const t0 = ctx.currentTime;
    const freqs = [420, 315, 240, 180];
    freqs.forEach((freq, i) => {
      const t = t0 + i * 0.09;
      const osc = ctx.createOscillator();
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(freq * 1.2, t);
      osc.frequency.exponentialRampToValueAtTime(freq * 0.4, t + 0.55);
      const filter = ctx.createBiquadFilter();
      filter.type = 'lowpass';
      filter.frequency.setValueAtTime(1800, t);
      filter.frequency.exponentialRampToValueAtTime(400, t + 0.5);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.07, t);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.6);
      osc.connect(filter);
      filter.connect(g);
      g.connect(ctx.destination);
      osc.start(t);
      osc.stop(t + 0.65);
    });
  }
}
